import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'

import { FeatureFlags } from '@/helpers/featureFlags'

/**
 * Whether the lot itself is Seller Collects, regardless of the flag.
 *
 * Separate from {@link useIsSellerCollectsSale} because a few rules are about
 * the money rather than the experience, and those hold whether or not the new
 * buyer experience is switched on. The convenience fee is one: it is what the
 * processor charges to move money to B-Stock, and on these lots B-Stock moves
 * none of it, so charging for it is wrong in production today — where the flag
 * is off.
 *
 * Not a hook, so the non-React callers that compute totals can use it too.
 */
export const isSellerCollectsLot = (
  listing: { sellerCollects?: boolean } | null | undefined
): boolean => listing?.sellerCollects === true

/**
 * Whether this listing is a Seller Collects sale the buyer experience applies
 * to: the flag is on and the listing says so.
 *
 * One source, because the answer gates behaviour in several places at once —
 * the bid and Buy Now modals, the autopay provider, the fee picker's caller —
 * and each surface that spelled the rule out again was a chance for them to
 * disagree. Wire refusal in particular is enforced both upstream in the
 * provider and in the picker; they have to reach the same verdict.
 */
export const useIsSellerCollectsSale = (
  listing: { sellerCollects?: boolean } | null | undefined
): boolean => {
  const sellerCollectsEnabled = useFlag(
    FeatureFlags.SELLER_COLLECTS_BUYER_EXPERIENCE
  )

  return sellerCollectsEnabled && isSellerCollectsLot(listing)
}

/**
 * Whether wire is refused for this sale.
 *
 * Wire pays for goods, and on a Seller Collects listing B-Stock collects none
 * of that — so it is withheld, both from the picker's radio and from the
 * autopay preference upstream of it.
 *
 * Except on a non-USD listing, where ACH, card and net terms are all
 * restricted already and wire is the only instrument left. Refusing it there
 * would leave no way to pay the B-Stock fee at all.
 *
 * A function rather than a rule repeated in both places: the picker and the
 * provider have to reach the same verdict, and comments saying so were what
 * stood in for the code doing so.
 */
export const isWireRefused = ({
  isSellerCollectsSale,
  currency,
}: {
  isSellerCollectsSale: boolean
  currency?: string | null
}): boolean => {
  const isNonUsd = !!currency && currency.toUpperCase() !== 'USD'

  return isSellerCollectsSale && !isNonUsd
}
